import { useMemo, useState } from "react";
import { apiErrorMessage, stopRecurringTemplate, updateRecurringTemplate } from "../../../api";
import OwnerBubble from "../OwnerBubble";
import { RepeatsCell } from "./icons";

// The "Recurring templates" view: one row per piece of recurring work.
// Stopping a template keeps its periods; it just makes no more.

const th = {
  textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.03em",
  color: "#676879", fontWeight: 600, padding: "10px 12px",
  borderBottom: "1px solid #e6e9ef", background: "#f7f8fa", position: "sticky", top: 0,
};
const td = { padding: "10px 12px", borderBottom: "1px solid #f1f3f6", fontSize: 13, verticalAlign: "middle" };

function monthLabel(iso) {
  const m = /^(\d{4})-(\d{2})/.exec(iso || "");
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
    .toLocaleDateString("en-CA", { month: "short", year: "numeric" });
}

export default function RecurringTemplatesView({
  templates = [], employees = [], query = "", clientFilter = "", ownerFilter = "",
  onEdit, onChanged,
}) {
  const [showStopped, setShowStopped] = useState(false);
  const [error, setError] = useState("");

  const nameOf = useMemo(() => {
    const m = {};
    for (const e of employees) m[Number(e.id)] = e.name || e.full_name || e.user?.username || "";
    return m;
  }, [employees]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates
      .filter((t) => showStopped || t.is_active)
      .filter((t) => !clientFilter || String(t.client) === String(clientFilter))
      .filter((t) => !ownerFilter || (t.owner_ids || []).map(String).includes(String(ownerFilter)))
      .filter((t) => !q || `${t.client_name} ${t.name} ${t.task_type_name || ""}`.toLowerCase().includes(q))
      .sort((a, b) => (a.client_name || "").localeCompare(b.client_name || "") || a.name.localeCompare(b.name));
  }, [templates, showStopped, clientFilter, ownerFilter, query]);

  const setActive = async (t, active) => {
    setError("");
    try {
      if (active) await updateRecurringTemplate(t.id, { is_active: true });
      else {
        if (!window.confirm(`Stop "${t.name}" for ${t.client_name}? Open periods stay; no new ones will be made.`)) return;
        await stopRecurringTemplate(t.id);
      }
      await onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not update the template."));
    }
  };

  return (
    <div style={{ flex: 1, overflow: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 0 8px" }}>
        <label style={{ fontSize: 12, color: "#676879", display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={showStopped} onChange={(e) => setShowStopped(e.target.checked)} />
          Show stopped templates
        </label>
        {error && <span role="alert" style={{ color: "#d83a52", fontSize: 12 }}>{error}</span>}
      </div>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={th}>Client</th>
            <th style={th}>Work item</th>
            <th style={{ ...th, textAlign: "center" }}>Repeats</th>
            <th style={th}>Started</th>
            <th style={th}>Due</th>
            <th style={th}>Owner</th>
            <th style={th}>Open periods</th>
            <th style={th}>Latest period</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td style={{ ...td, color: "#9699a6" }} colSpan={9}>
              No recurring templates yet. Use “New task” and pick how often it repeats.
            </td></tr>
          )}
          {rows.map((t) => (
            <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.55 }}>
              <td style={td}>{t.client_name}</td>
              <td style={td}>{t.name}</td>
              <td style={td}><RepeatsCell recurrence={t.recurrence} /></td>
              <td style={td}>{monthLabel(t.first_period_start)}</td>
              <td style={td}>{t.due_days_after} days after period end</td>
              <td style={td}>
                <div style={{ display: "flex", gap: 4 }}>
                  {(t.owner_ids || []).map((id) => (
                    <OwnerBubble key={id} owner={nameOf[Number(id)] || ""} size={24} />
                  ))}
                </div>
              </td>
              <td style={td}>{t.open_period_count}</td>
              <td style={td}>{t.latest_period_label}</td>
              <td style={{ ...td, whiteSpace: "nowrap", textAlign: "right" }}>
                <button
                  type="button" onClick={() => onEdit(t)}
                  style={{ border: "none", background: "none", color: "#0073ea", cursor: "pointer", fontSize: 13 }}
                >Edit</button>
                <button
                  type="button" onClick={() => setActive(t, !t.is_active)}
                  style={{
                    border: "none", background: "none", cursor: "pointer", fontSize: 13,
                    color: t.is_active ? "#d83a52" : "#00854d",
                  }}
                >{t.is_active ? "Stop" : "Restart"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
